import { RootState } from 'redux/store'
import { FREQUENCY } from '@classy/campaign-page-blocks'

import flowSlice from './flow-slice'
import orderAmountSlice from 'modules/order/state/amount/order-amount-slice'
import orderFrequencySlice from 'modules/order/state/frequency/order-frequency-slice'

import { createListenerMiddleware, isAnyOf } from '@reduxjs/toolkit'
import { setPaymentMethod, setPaymentType } from 'redux/features/paymentDetails'
import { BANK_DETAILS_STEP_NAME } from './utils/steps/bank-details'
import { CARD_DETAILS_STEP_NAME } from './utils/steps/card-details'
import { ADDITIONAL_DONOR_INFORMATION_STEP_NAME } from './utils/steps/additional-donor-information'
import { THANK_YOU_STEP_NAME } from './utils/steps/thank-you'
import { RECURRING_NUDGE_STEP_NAME } from './utils/steps/recurring-nudge'
import { getAnalyticsInstance } from '../../analytics/analytics'
import { fetchDesignation } from 'redux/services/designation'

export const RECURRING_NUDGE_THRESHOLD = 201 // eventually this should come from server

/**
 * This middleware listens to updates to enable/disable the recurringNudge step
 */
export const recurringNudgeStepMiddleware = createListenerMiddleware<RootState>()

recurringNudgeStepMiddleware.startListening({
  matcher: isAnyOf(orderFrequencySlice.actions.setValue, orderAmountSlice.actions.setValue),
  effect: (_, listenerApi) => {
    const state = listenerApi.getState()
    const recurringNudgeIsEnabledOnCampaign = state.config.recurringNudge.enabled

    const isMonthlyVisible =
      state.config.frequency.options.find((option) => option.name === 'monthly')?.isVisible ?? false

    if (recurringNudgeIsEnabledOnCampaign) {
      const frequencyIsOneTime = state.order.frequency.value === FREQUENCY.ONE_TIME
      const amountIsLessThanThreshold = state.order.amount.value < RECURRING_NUDGE_THRESHOLD

      if (frequencyIsOneTime && amountIsLessThanThreshold && isMonthlyVisible) {
        listenerApi.dispatch(flowSlice.actions.enableStep(RECURRING_NUDGE_STEP_NAME))
      } else {
        listenerApi.dispatch(flowSlice.actions.disableStep(RECURRING_NUDGE_STEP_NAME))
      }
    }
  },
})

/**
 * This middleware listens to updates in the selected paymentType to enable the respective flow step
 */
export const flowOrderPaymentMethodListener = createListenerMiddleware<RootState>()

flowOrderPaymentMethodListener.startListening({
  actionCreator: setPaymentType,
  effect: (action, listenerApi) => {
    switch (action.payload) {
      case 'ACH': {
        listenerApi.dispatch(flowSlice.actions.enableStep(BANK_DETAILS_STEP_NAME))
        listenerApi.dispatch(flowSlice.actions.disableStep(CARD_DETAILS_STEP_NAME))

        break
      }

      case 'CC': {
        listenerApi.dispatch(flowSlice.actions.disableStep(BANK_DETAILS_STEP_NAME))
        listenerApi.dispatch(flowSlice.actions.enableStep(CARD_DETAILS_STEP_NAME))

        break
      }

      default: {
        listenerApi.dispatch(flowSlice.actions.disableStep(BANK_DETAILS_STEP_NAME))
        listenerApi.dispatch(flowSlice.actions.disableStep(CARD_DETAILS_STEP_NAME))
      }
    }
  },
})

/**
 * When the donor gets to certain steps in the flow, we want to disable all the previous ones.
 * For example the first post-conversion step and the "thank-you" step.
 */
export const flowCurrentStepListener = createListenerMiddleware<RootState>()

flowCurrentStepListener.startListening({
  actionCreator: flowSlice.actions.goToStep,
  effect: (action, listenerApi) => {
    const dispatchDisableStepsBefore = (stepName: string) => {
      listenerApi.dispatch(flowSlice.actions.disableStepsBefore(stepName))
    }

    const originalFlowState = listenerApi.getOriginalState().flow

    const toStep = originalFlowState.stepsMap[action.payload]
    const fromStep = originalFlowState.stepsMap[originalFlowState.currentStepName]

    const firstPostConversionStep =
      originalFlowState.stepsMap[ADDITIONAL_DONOR_INFORMATION_STEP_NAME]

    /**
     * Disable all pre-conversion steps
     */
    if (
      fromStep.index < firstPostConversionStep.index &&
      toStep.index >= firstPostConversionStep.index
    ) {
      dispatchDisableStepsBefore(ADDITIONAL_DONOR_INFORMATION_STEP_NAME)
    }

    /**
     * Disable all steps before the thank-you step
     */
    if (toStep.name === THANK_YOU_STEP_NAME) {
      dispatchDisableStepsBefore(THANK_YOU_STEP_NAME)

      return
    }
  },
})

/**
 * Middleware to track payment method details when setPaymentMethod is called.
 */
export const paymentMethodListenerMiddleware = createListenerMiddleware<RootState>()

paymentMethodListenerMiddleware.startListening({
  actionCreator: setPaymentMethod,
  effect: async (action, listenerApi) => {
    const state = listenerApi.getState()

    const designationId = state.config.designation.designationId

    const designation = await listenerApi
      .dispatch(fetchDesignation.initiate({ designationId }))
      .unwrap()

    const analytics = getAnalyticsInstance()

    const { dcf, amount, currency, paymentDetails } = state.order
    const { totalAmount } = dcf.value
    const { type: paymentType } = paymentDetails

    const currencyCode = currency.value || 'USD'
    const totalValue = totalAmount || amount.value

    // Trigger GA4 'add_payment_info' event
    analytics.track('add_payment_info', {
      payment_type: paymentType,
      currency: currencyCode,
      value: totalValue,
      campaign_id: state.config.campaignId,
      campaign_name: state.config.campaignName,
      organization_id: state.config.organization.id,
      organization_name: state.config.organization.name,
      items: [
        {
          price: totalValue,
          item_name: 'Donation',
          item_category: 'donation',
          item_category2: state.config.designation.designationId,
          item_category3: designation.name,
          item_category4: state.order.frequency.value,
        },
      ],
    })
  },
})
